import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lead } from '@/types/lead';
import { updateLead } from '@/services/leads/leadMutations';
import { toast } from '@/hooks/use-toast';
import { formatEmailForDisplay } from '@/utils/emailUtils';

interface EditLeadDialogProps {
  lead: Lead;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLeadUpdated: () => void;
}

const EditLeadDialog: React.FC<EditLeadDialogProps> = ({ lead, open, onOpenChange, onLeadUpdated }) => {
  const [name, setName] = useState(lead.name || '');
  const [category, setCategory] = useState(lead.category || '');
  const [address, setAddress] = useState(lead.address || '');
  const [phone, setPhone] = useState(lead.phone || '');
  const [email, setEmail] = useState(formatEmailForDisplay(lead.email));
  const [website, setWebsite] = useState(lead.website || '');
  const [isSaving, setIsSaving] = useState(false);

  // Reset form when dialog opens with new lead data
  React.useEffect(() => {
    if (open) {
      setName(lead.name || '');
      setCategory(lead.category || '');
      setAddress(lead.address || '');
      setPhone(lead.phone || '');
      setEmail(formatEmailForDisplay(lead.email));
      setWebsite(lead.website || '');
    }
  }, [open, lead]);

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Name is required', variant: 'destructive' });
      return;
    }

    setIsSaving(true);
    try {
      await updateLead({
        ...lead,
        name: name.trim(),
        category: category.trim() || undefined,
        address: address.trim() || undefined,
        phone: phone.trim() || undefined,
        email: email.trim() || undefined,
        website: website.trim() || undefined,
      });

      toast({ title: 'Lead updated successfully' });
      onOpenChange(false);
      onLeadUpdated();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast({ title: 'Failed to update lead', variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Lead</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name *</Label>
            <Input id="edit-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Business name" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-category">Category</Label>
            <Input id="edit-category" value={category} onChange={(e) => setCategory(e.target.value)} placeholder="e.g. Restaurant, Retail" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-address">Address</Label>
            <Input id="edit-address" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Full address" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-phone">Phone</Label>
            <Input id="edit-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Phone number" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-email">Email</Label>
            <Input id="edit-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email address" />
          </div>

          <div className="space-y-2">
            <Label htmlFor="edit-website">Website</Label>
            <Input id="edit-website" value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="https://example.com" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>Cancel</Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditLeadDialog;
